import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  CircleNotch, UploadSimple, FileXls, FileCsv, WarningCircle, XCircle,
  CheckCircle, ArrowCounterClockwise, Copy,
} from "@phosphor-icons/react";

function downloadBlob(content, filename, type) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([content], { type }));
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

const FIELD_LABELS = [
  ["organization_name", "Company/Organization Name"],
  ["website", "Company Website"],
  ["linkedin_url", "LinkedIn Company Page"],
  ["general_office_phone", "General Office Phone"],
  ["general_email", "General Email"],
  ["notes", "Additional Prospect Notes"],
  ["lead_source_name", "Lead Source"],
  ["relationship_stage", "Lifecycle Stage"],
  ["lead_source_detail", "Lead Source Details"],
  ["assigned_salesperson_name", "Assigned Salesperson"],
  ["date_entered", "Date Entered Into CRM"],
  ["originator_name", "TFS Originator"],
];

export default function ImportProspectDialog({ open, onClose, onDone }) {
  const [step, setStep] = useState("start");
  const [busy, setBusy] = useState(false);
  const [batch, setBatch] = useState(null);
  const [confirmDup, setConfirmDup] = useState(false);
  const [unknownHeadings, setUnknownHeadings] = useState(null);
  const [created, setCreated] = useState(null);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const downloadTemplate = async (fmt) => {
    try {
      const res = await api.get(`/prospect-import/template?format=${fmt}`, { responseType: "blob" });
      downloadBlob(res.data, `prospect_import_template.${fmt}`, res.data.type);
    } catch (e) {
      toast.error("Template could not be downloaded — please try again");
    }
  };

  const runValidate = async (filename, b64, ignoreUnknown) => {
    const { data } = await api.post("/prospect-import/validate",
      { filename, content_base64: b64, ignore_unknown_headings: ignoreUnknown });
    if (data.needs_confirmation) {
      setUnknownHeadings({ filename, b64, headings: data.unknown_headings });
      return;
    }
    setUnknownHeadings(null);
    setBatch(data);
    setConfirmDup(false);
    setStep("preview");
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const rd = new FileReader();
        rd.onload = () => resolve(String(rd.result).split(",")[1]);
        rd.onerror = reject;
        rd.readAsDataURL(file);
      });
      await runValidate(file.name, b64, false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/prospect-import/${batch.id}/commit`, { confirm_duplicate: confirmDup });
      setCreated(data);
      setStep("done");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setStep("start"); setBatch(null); setCreated(null); setConfirmDup(false); };
  const close = () => { reset(); onClose(); };
  const hasErrors = (batch?.errors || []).length > 0;
  const stageLabel = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "—");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-sm sm:max-w-3xl" data-testid="prospect-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#032852]">Import Prospect from Excel</DialogTitle>
          <DialogDescription>
            One prospect per file. Nothing is saved until you confirm the review — existing records are never
            overwritten or merged.
          </DialogDescription>
        </DialogHeader>

        {step === "start" && (
          <div className="space-y-5">
            {unknownHeadings && (
              <div className="rounded-sm border border-amber-300 bg-amber-50 p-4" data-testid="prospect-unknown-headings">
                <p className="text-xs font-bold uppercase tracking-wide text-amber-900">Unrecognized column headings</p>
                <p className="mt-1 text-xs text-amber-900">
                  {unknownHeadings.headings.map((h) => `"${h}"`).join(", ")} — these columns are not part of the
                  template and will NOT be imported. Correct the headings and re-upload, or confirm to ignore them.
                </p>
                <div className="mt-3 flex gap-2">
                  <Button type="button" size="sm" disabled={busy} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]"
                    onClick={async () => {
                      setBusy(true);
                      try { await runValidate(unknownHeadings.filename, unknownHeadings.b64, true); }
                      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      finally { setBusy(false); }
                    }} data-testid="prospect-ignore-unknown-btn">
                    Ignore these columns and continue
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={() => setUnknownHeadings(null)}>
                    I will correct the file
                  </Button>
                </div>
              </div>
            )}
            <div className="rounded-sm border border-[#E4E4E7] bg-[#FAFAFA] p-4">
              <p className="text-xs font-bold uppercase tracking-wide text-[#52525B]">1 — Download the template</p>
              <p className="mt-1 text-xs text-[#6D6D70]">
                Company/Organization Name is the only required column. The Excel version includes an Instructions
                sheet with required fields, allowed dropdown values and defaults.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button type="button" variant="outline" className="rounded-sm" onClick={() => downloadTemplate("xlsx")} data-testid="prospect-template-xlsx">
                  <FileXls size={16} className="text-[#1D6F42]" /> Excel template
                </Button>
                <Button type="button" variant="outline" className="rounded-sm" onClick={() => downloadTemplate("csv")} data-testid="prospect-template-csv">
                  <FileCsv size={16} className="text-[#032852]" /> CSV template
                </Button>
              </div>
            </div>
            <div className="rounded-sm border border-[#E4E4E7] bg-[#FAFAFA] p-4">
              <p className="text-xs font-bold uppercase tracking-wide text-[#52525B]">2 — Upload the completed file</p>
              <p className="mt-1 text-xs text-[#6D6D70]">.xlsx or .csv — you will review the parsed data before anything is created.</p>
              <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={onFile} data-testid="prospect-import-file-input" />
              <Button type="button" disabled={busy} className="mt-3 rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]"
                onClick={() => fileRef.current?.click()} data-testid="prospect-import-upload-btn">
                {busy ? <CircleNotch size={18} className="animate-spin" /> : <UploadSimple size={16} weight="bold" />} Upload file
              </Button>
            </div>
          </div>
        )}

        {step === "preview" && batch && (
          <div className="space-y-4" data-testid="prospect-import-preview">
            {(batch.errors || []).length > 0 && (
              <div className="rounded-sm border border-red-200 bg-red-50 p-3" data-testid="prospect-import-errors">
                <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-red-700">
                  <XCircle size={14} weight="bold" /> Fix these before importing
                </p>
                {batch.errors.map((m, i) => <p key={i} className="mt-1 text-xs text-red-700">{m}</p>)}
              </div>
            )}
            {(batch.warnings || []).length > 0 && (
              <div className="rounded-sm border border-amber-300 bg-amber-50 p-3" data-testid="prospect-import-warnings">
                <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-amber-900">
                  <WarningCircle size={14} weight="bold" /> Warnings
                </p>
                {batch.warnings.map((m, i) => <p key={i} className="mt-1 text-xs text-amber-900">{m}</p>)}
              </div>
            )}
            {(batch.defaults_applied || []).length > 0 && (
              <div className="rounded-sm border border-[#032852]/20 bg-[#032852]/5 p-3" data-testid="prospect-import-defaults">
                <p className="text-xs font-bold uppercase tracking-wide text-[#032852]">Defaults applied</p>
                {batch.defaults_applied.map((m, i) => <p key={i} className="mt-1 text-xs text-[#032852]">{m}</p>)}
              </div>
            )}
            {batch.duplicate && (
              <div className="rounded-sm border border-orange-300 bg-orange-50 p-3" data-testid="prospect-import-duplicate">
                <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-orange-800">
                  <Copy size={14} weight="bold" /> Possible duplicate
                </p>
                <p className="mt-1 text-xs text-orange-900">
                  A prospect named <span className="font-semibold">{batch.duplicate.organization_name}</span> already
                  exists. Nothing is ever merged or overwritten — confirming creates a separate record flagged for
                  administrator review.
                </p>
                <label className="mt-2 flex items-center gap-2 text-xs font-semibold text-orange-900">
                  <Checkbox checked={confirmDup} onCheckedChange={(v) => setConfirmDup(Boolean(v))} data-testid="confirm-duplicate-checkbox" />
                  Create anyway as a separate prospect
                </label>
              </div>
            )}
            <div className="rounded-sm border border-[#E4E4E7]">
              <p className="border-b border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-[#52525B]">Parsed prospect</p>
              <dl className="grid grid-cols-1 gap-x-4 px-3 py-2 sm:grid-cols-2">
                {FIELD_LABELS.map(([k, label]) => (
                  <div key={k} className="flex justify-between gap-3 border-b border-[#F4F4F5] py-1.5 text-xs">
                    <dt className="font-semibold text-[#52525B]">{label}</dt>
                    <dd className="whitespace-pre-wrap text-right text-[#09090B]" data-testid={`parsed-${k}`}>
                      {k === "relationship_stage" ? stageLabel(batch.fields?.[k]) : (batch.fields?.[k] || "—")}
                    </dd>
                  </div>
                ))}
              </dl>
              <p className="border-t border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-[#52525B]">Primary contact</p>
              <div className="px-3 py-2 text-xs" data-testid="parsed-contact">
                {batch.contact ? (
                  <p>
                    <span className="font-semibold text-[#09090B]">{batch.contact.first_name} {batch.contact.last_name}</span>
                    {batch.contact.title ? ` — ${batch.contact.title}` : ""}
                    {batch.contact.email ? ` · ${batch.contact.email}` : ""}
                    {batch.contact.office_phone ? ` · Office ${batch.contact.office_phone}` : ""}
                    {batch.contact.mobile_phone ? ` · Mobile ${batch.contact.mobile_phone}` : ""}
                  </p>
                ) : <p className="text-[#6D6D70]">None provided — the prospect is created without a primary contact.</p>}
              </div>
              {batch.fields?.intelligence_preview && (
                <>
                  <p className="border-t border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-[#52525B]">Prospect Intelligence (editable after import)</p>
                  <p className="max-h-32 overflow-auto whitespace-pre-wrap px-3 py-2 text-xs text-[#52525B]" data-testid="parsed-intelligence">
                    {batch.fields.intelligence_preview}
                  </p>
                </>
              )}
            </div>
            <DialogFooter className="flex-wrap gap-2">
              <Button type="button" variant="outline" className="rounded-sm" onClick={reset} data-testid="prospect-reupload-btn">
                <ArrowCounterClockwise size={16} /> Re-upload corrected file
              </Button>
              <Button type="button" variant="outline" className="rounded-sm" onClick={close}>Cancel</Button>
              <Button type="button" disabled={busy || hasErrors || (batch.duplicate && !confirmDup)} onClick={commit}
                className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="confirm-prospect-import-btn">
                {busy ? <CircleNotch size={18} className="animate-spin" /> : null} Create prospect
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "done" && created && (
          <div className="space-y-4" data-testid="prospect-import-done">
            <div className="rounded-sm border border-[#65AB15]/30 bg-[#65AB15]/10 p-4">
              <p className="flex items-center gap-2 text-sm font-bold text-[#4c840f]">
                <CheckCircle size={18} weight="bold" /> Prospect created
              </p>
              <p className="mt-1 text-xs text-[#4c840f]">
                {created.organization_name} was imported{created.contact_created ? " with its primary contact" : ""}.
                {created.intelligence_imported ? " Prospect Intelligence was placed in the Intelligence section and stays fully editable." : ""}
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-sm" onClick={close}>Close</Button>
              <Button type="button" className="rounded-sm bg-[#032852] font-semibold text-white hover:bg-[#032852]/90"
                onClick={() => { close(); navigate(`/prospects/${created.prospect_id}`); }} data-testid="view-imported-prospect-btn">
                View prospect
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
